import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { ButtonWithIcon } from "../../../components/buttons";
import {
  Box,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import ambassadorHOC from "../ambassador-hoc";
import AmbassadorFilterOptions from "./filter-options";
import { AmbassadorListTable } from "./table";
import { AMBASSADOR_LIST_COLUMNS } from "./table-config";
import { downloadAmbassadorListCsv } from "./download-utils";
import Searchbox from "../../../components/searchbox";
import { getAmbassadorsForDownload, restoreAmbassador } from "../../../apis/ambassadors.api";
import { AppAlertBox } from "../../../components/modals/alert-box";
import { showNotification } from "../../../services/utils";

function AmbassadorsList({
  onAddAmbassadorClick,
  getAmbassadorList,
  ambassadorList,
  toggleMenuOptions,
}) {
  const dispatch = useDispatch();
  const { selectedPageSize, searchValue, selectedPage, selectedFilters, isDeleted } =
    useSelector((state: any) => state.ambassadors.list.filters);

  const [showUnarchiveConfirm, setShowUnarchiveConfirm] = useState(false);
  const [selectedAmbassadorId, setSelectedAmbassadorId] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const listParams = {
    selectedPage,
    selectedPageSize,
    searchValue,
    selectedFilters,
    isDeleted,
  };

  useEffect(() => {
    getAmbassadorList(listParams);
  }, []);

  const handlePageFilters = ({ selectedPage, selectedPageSize }) => {
    getAmbassadorList({
      selectedPage,
      selectedPageSize,
      searchValue,
      selectedFilters,
      isDeleted,
    });
  };

  const handleSearchValueChange = (value = "") => {
    getAmbassadorList({
      searchValue: value,
      selectedPage: 1,
      selectedPageSize,
      selectedFilters,
      isDeleted,
    });
  };

  const handleArchivedOnlyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    getAmbassadorList({
      selectedPage: 1,
      selectedPageSize,
      searchValue,
      selectedFilters,
      isDeleted: event.target.checked,
    });
  };

  const handleUnarchiveClick = (id: string) => {
    setSelectedAmbassadorId(id);
    setShowUnarchiveConfirm(true);
  };

  const handleUnarchiveConfirm = async () => {
    if (!selectedAmbassadorId) return;

    try {
      const res: any = await dispatch(restoreAmbassador(selectedAmbassadorId));
      if (res.success) {
        showNotification({
          message: "Ambassador unarchived successfully",
          type: "success",
        });
        setShowUnarchiveConfirm(false);
        setSelectedAmbassadorId(null);
        getAmbassadorList(listParams);
      } else {
        showNotification({
          message: res.message || "Failed to restore ambassador",
          type: "error",
        });
      }
    } catch (err) {
      showNotification({
        message: "Failed to unarchive ambassador",
        type: "error",
      });
    }
  };

  const handleUnarchiveCancel = () => {
    setShowUnarchiveConfirm(false);
    setSelectedAmbassadorId(null);
  };

  const handleDownload = async () => {
    const total = ambassadorList?.total;
    if (!total) return;

    setIsDownloading(true);

    try {
      const res: any = await getAmbassadorsForDownload({
        total,
        searchValue,
        selectedFilters,
        isDeleted,
      });

      if (res?.success && res?.data?.length) {
        downloadAmbassadorListCsv(res.data);
      } else {
        showNotification({
          message: res?.message || "Failed to download ambassador list",
          type: "error",
        });
      }
    } catch {
      showNotification({
        message: "Failed to download ambassador list",
        type: "error",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Stack>
      <Grid container alignItems={"center"} spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Searchbox
            onEnter={handleSearchValueChange}
            value={searchValue}
            onClear={() => handleSearchValueChange("")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={8}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="flex-end"
            flexWrap="wrap"
          >
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(isDeleted)}
                  onChange={handleArchivedOnlyChange}
                  color="primary"
                />
              }
              label="Archived Users"
            />
            <ButtonWithIcon
              handleOnClick={onAddAmbassadorClick}
              text={"ADD"}
              icon={{ position: "startIcon", name: "add" }}
            />
          </Stack>
        </Grid>
      </Grid>

      <Grid container spacing={2} direction="column">
        <Grid item sx={{ maxWidth: "100% !important" }} xs={12}>
          <AmbassadorFilterOptions />
        </Grid>
        <Grid item xs={12}>
          {Boolean(ambassadorList?.total) && (
            <Stack direction="row" justifyContent="flex-end" mb={1}>
              <ButtonWithIcon
                handleOnClick={isDownloading ? undefined : handleDownload}
                text={isDownloading ? "DOWNLOADING..." : "DOWNLOAD"}
                icon={{ position: "startIcon", name: "download" }}
                muiIcon={<FileDownloadOutlinedIcon />}
                sx={{
                  opacity: isDownloading ? 0.7 : 1,
                  pointerEvents: isDownloading ? "none" : "auto",
                }}
              />
            </Stack>
          )}
          <AmbassadorListTable
            list={ambassadorList}
            toggleMenuOptions={toggleMenuOptions}
            columns={AMBASSADOR_LIST_COLUMNS}
            handlePageFilters={handlePageFilters}
            appliedFilters={{
              selectedPageSize,
              searchValue,
              selectedPage,
              selectedFilters,
            }}
            isDeleted={Boolean(isDeleted)}
            onUnarchiveClick={handleUnarchiveClick}
          />
        </Grid>
      </Grid>

      <AppAlertBox
        isModalOpen={showUnarchiveConfirm}
        handleClose={handleUnarchiveCancel}
        onConfirm={handleUnarchiveConfirm}
        onCancel={handleUnarchiveCancel}
        message={
          <Box>
            <Typography fontWeight={600} mb={1.5}>
              Are you sure you want to unarchive this ambassador?
            </Typography>
            <Typography fontSize="14px" color="#6B7280" lineHeight={1.6}>
              <strong>Unarchive:</strong> This will restore the ambassador and they will no longer
              appear in archived ambassadors.
            </Typography>
          </Box>
        }
        maxModalWidth="sm"
      />
    </Stack>
  );
}

export default ambassadorHOC(React.memo(AmbassadorsList));
